import React from 'react';
import {Box, CircularProgress} from '@mui/material';
import {
  Button,
  Body,
  Label,
  Icon,
  designSystemToken,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {SwapMatch} from '@/types/swapMatch';
import styles from './SwapMatchPostsGrid.module.scss';

interface SwapMatchPostsGridProps {
  testID?: string;
  posts: SwapMatch[];
  selectedPostId: string | null;
  onPostSelect: (post: SwapMatch) => void;
  isLoading?: boolean;
}

function SwapMatchPostCard({
  post,
  isSelected,
  onClick,
}: {
  post: SwapMatch;
  isSelected: boolean;
  onClick: () => void;
}) {
  const handleInstagramClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card selection
    if (post.url) {
      window.open(post.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box
      className={`${styles.postCard} ${isSelected ? styles.selected : ''}`}
      onClick={onClick}
      data-testid={`swap-match-post-${post.id}`}
    >
      {post.thumbnailUrl ? (
        <Box className={styles.mediaContainer}>
          <img
            src={post.thumbnailUrl}
            alt={post.body || 'Post'}
            className={styles.media}
            loading="lazy"
          />
          {post.url && (
            <Button
              appearance="overlay"
              mode="elevated"
              size="medium"
              onClick={handleInstagramClick}
              className={styles.instagramButton}
              data-testid={`instagram-link-${post.id}`}
              title={translate('Open in Instagram')}
            >
              <Icon name="Social-Instagram" size="medium" appearance="black" />
            </Button>
          )}
        </Box>
      ) : (
        <Box className={styles.placeholderMedia}>
          <Label size="sm" color={designSystemToken('semantic.fg.secondary')}>
            {translate('No preview available')}
          </Label>
        </Box>
      )}

      <Box className={styles.postInfo}>
        <Label size="sm" className={styles.postMetric}>
          <Icon
            name="Navigation-Heart-Fill"
            size="small"
            appearance="neutral"
          />{' '}
          {post.likeCount.toLocaleString()}
        </Label>
        <Label size="sm" className={styles.postMetric}>
          <Icon name="Actions-Comment2" size="small" appearance="neutral" />{' '}
          {post.commentCount.toLocaleString()}
        </Label>
        {post.viewCount && post.viewCount > 0 && (
          <Label size="sm" className={styles.postMetric}>
            <Icon name="Features-Eye" size="small" appearance="neutral" />{' '}
            {post.viewCount.toLocaleString()}
          </Label>
        )}
      </Box>

      {post.body && (
        <Label
          size="sm"
          color={designSystemToken('semantic.fg.secondary')}
          className={styles.postBody}
        >
          {post.body.length > 80
            ? `${post.body.substring(0, 80)}...`
            : post.body}
        </Label>
      )}
    </Box>
  );
}

function SwapMatchPostsGrid({
  testID = 'swap-match-posts-grid',
  posts,
  selectedPostId,
  onPostSelect,
  isLoading = false,
}: SwapMatchPostsGridProps) {
  if (isLoading) {
    return (
      <Box
        className={styles.loadingContainer}
        data-testid={`${testID}-loading`}
      >
        <CircularProgress />
        <Body
          size="md"
          color={designSystemToken('semantic.fg.secondary')}
          sx={{marginTop: 2}}
        >
          {translate('Loading posts...')}
        </Body>
      </Box>
    );
  }

  if (posts.length === 0) {
    return (
      <Box className={styles.emptyContainer} data-testid={`${testID}-empty`}>
        <Body size="lg" color={designSystemToken('semantic.fg.secondary')}>
          {translate('No posts found for this creator')}
        </Body>
      </Box>
    );
  }

  return (
    <Box className={styles.container} data-testid={testID}>
      <Box className={styles.grid}>
        {posts.map((post) => (
          <SwapMatchPostCard
            key={post.id}
            post={post}
            isSelected={post.id === selectedPostId}
            onClick={() => onPostSelect(post)}
          />
        ))}
      </Box>
    </Box>
  );
}

export default SwapMatchPostsGrid;
